/**
 * Strategy Lab Coach types — FE ↔ BE `/strategy-rd/v1/coach/*`.
 * Canonical: docs/roadmap/STRATEGY_RD_COACH_FE_HANDOFF.md (BE full contract).
 */

import type {
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerEntrySlot,
  StrategyRdComposerSetupSlot,
  StrategyRdHeadlineStats,
  StrategyRdHoldoutSplit,
  StrategyRdLogicPath,
  StrategyRdStability,
  StrategyRdOhlcStats,
  StrategyRdOutcomeSummary,
  StrategyRdSweepTable,
} from "./strategyRd";

export const STRATEGY_RD_COACH_CATALOG_BADGE = "Uses Tessera catalog only.";

/** Product name shown in the Lab coach panel (Trading Edge System). */
export const STRATEGY_RD_COACH_DISPLAY_NAME = "TES";

/** Short expansion under the TES mark — keep UI copy in sync with this. */
export const STRATEGY_RD_COACH_DISPLAY_EXPANSION = "Trading Edge System";

export const STRATEGY_RD_COACH_HISTORY_LIMIT = 8;

export const STRATEGY_RD_COACH_ERROR_SLUGS = {
  weeklyTurns: "strategy_rd.coach.quota.weekly_turns",
  weeklyTokens: "strategy_rd.coach.quota.weekly_tokens",
  tesUnavailable: "strategy_rd.coach.tes_unavailable",
  unknownConcept: "strategy_rd.coach.unknown_concept",
  invalidPatch: "strategy_rd.coach.invalid_patch",
  threadNotFound: "strategy_rd.coach.thread_not_found",
  threadLimit: "strategy_rd.coach.thread_limit",
  messageLimit: "strategy_rd.coach.message_limit",
  intakeRequired: "strategy_rd.coach.intake_required",
  unknownPack: "strategy_rd.coach.unknown_pack",
} as const;

export type StrategyRdCoachGoal =
  | "build"
  | "why_no_hits"
  | "improve"
  | "explain_matches"
  | "what_else"
  | "explain"
  | "what_is"
  | "journal"
  | string;

export interface StrategyRdCoachUsage {
  plan: string;
  turns_used: number;
  turns_cap: number;
  turns_remaining: number;
  tokens_used: number;
  tokens_cap: number;
  tokens_remaining: number;
  soft_warn: boolean;
  window: string;
  week_start?: string | null;
  /** "credits" (paid plans: AI credits this billing month) or "prompts" (Free: prompts this week). */
  mode?: "credits" | "prompts" | null;
  /** Lab backtests (the Lab and TES share them): Free per rolling week, paid per billing month. */
  backtests_window?: "week" | "month" | null;
  backtests_used?: number | null;
  backtests_cap?: number | null;
  backtests_remaining?: number | null;
  disclaimer?: string | null;
  product_name?: string | null;
  product_expand?: string | null;
}

export interface StrategyRdCoachStyle {
  id: string;
  name: string;
  pack_id?: string | null;
  description?: string | null;
  blurb?: string | null;
  family?: string | null;
  default_candle_type?: string | null;
  default_session_ids?: string[] | null;
  alt_pack_ids?: string[] | null;
  families?: string[] | null;
  goals?: string[] | null;
}

export interface StrategyRdCoachStylesResponse {
  styles: StrategyRdCoachStyle[];
  packs: Array<Record<string, unknown>>;
  catalog_only: boolean;
}

export interface StrategyRdCoachChip {
  id: string;
  label: string;
  pack_id?: string | null;
  style_id?: string | null;
  goal?: string | null;
  faq_id?: string | null;
}

export interface StrategyRdCoachOrientationCta {
  type: string;
  label?: string | null;
}

export interface StrategyRdCoachOrientationFaq {
  id: string;
  label: string;
  category?: string | null;
  answer_markdown: string;
  suggested_next?: string[] | null;
  cta?: StrategyRdCoachOrientationCta | null;
}

export interface StrategyRdCoachOrientationResponse {
  faqs: StrategyRdCoachOrientationFaq[];
  starter_chips: StrategyRdCoachChip[];
  product_name?: string | null;
  product_expand?: string | null;
  disclaimer?: string | null;
  catalog_only?: boolean;
}

export interface StrategyRdCoachLabSeed {
  style_id?: string | null;
  candle_type?: string | null;
  alt_candle_types?: string[] | null;
  lookback_days?: number | null;
  session_ids?: string[] | null;
  example_symbols?: string[] | null;
  suggested_intake?: StrategyRdCoachIntakeRequest | null;
}

/** @deprecated Prefer StrategyRdPreviewEmptyState from strategyRd.ts */
export type {
  StrategyRdPreviewEmptyState,
  StrategyRdPreviewEmptyStateAction,
} from "./strategyRd";

/** Empty-state prompt tiles (handoff examples) — click fills input / sets goal. */
export const STRATEGY_RD_COACH_STARTER_PROMPTS: StrategyRdCoachChip[] = [
  {
    id: "explain_setup",
    label: "Explain the setup and confluences in my composer",
    goal: "explain",
  },
  {
    id: "mix_fvg_london",
    label: "Mix FVG with a liquidity sweep for London",
    goal: "build",
  },
  {
    id: "why_no_hits",
    label: "Why did my last scan get so few hits?",
    goal: "why_no_hits",
  },
  {
    id: "improve",
    label: "How can I improve this recipe?",
    goal: "improve",
  },
];

export interface StrategyRdCoachFocus {
  style_id?: string | null;
  pack_id?: string | null;
  families?: string[] | null;
  roles?: string[] | null;
  /** FE funnel stage — BE/TES may use for next-step chips. */
  lab_stage?: string | null;
  preview_committed?: boolean | null;
  confluence_count?: number | null;
  match_count?: number | null;
  [key: string]: unknown;
}

export interface StrategyRdCoachComposerBody {
  candle_type?: string | null;
  setup?: StrategyRdComposerSetupSlot | Record<string, unknown> | null;
  confluences?: Array<StrategyRdComposerConfluenceSlot | string | Record<string, unknown>> | null;
  entry?: StrategyRdComposerEntrySlot | Record<string, unknown> | null;
  multi_tf?: boolean | null;
  session_ids?: string[] | null;
  [key: string]: unknown;
}

export interface StrategyRdCoachIntakeRequest {
  style_id?: string | null;
  pack_id?: string | null;
  symbol?: string | null;
  candle_type?: string | null;
  session_ids?: string[] | null;
  trade_side?: string | null;
  polarity?: string | null;
  goal?: string | null;
}

export interface StrategyRdCoachIntakeResponse {
  style_id?: string | null;
  pack_id?: string | null;
  goal?: string | null;
  composer?: StrategyRdCoachComposerBody | null;
  scan_seed?: Record<string, unknown> | null;
  normalized?: Record<string, unknown> | null;
  suggested_chips?: StrategyRdCoachChip[] | null;
  goals?: StrategyRdCoachChip[] | null;
  catalog_only?: boolean;
}

export interface StrategyRdCoachHistoryMessage {
  role: "user" | "assistant" | string;
  content: string;
}

export interface StrategyRdCoachScanSummary {
  ohlc_stats?: StrategyRdOhlcStats | Record<string, unknown> | null;
  logic_path?: StrategyRdLogicPath | Record<string, unknown> | null;
}

export interface StrategyRdCoachTurnRequest {
  message: string;
  goal?: string | null;
  composer?: StrategyRdCoachComposerBody | Record<string, unknown> | null;
  scan_summary?: StrategyRdCoachScanSummary | null;
  outcome_summary?: StrategyRdOutcomeSummary | Record<string, unknown> | null;
  history?: StrategyRdCoachHistoryMessage[] | null;
  session_id?: string | null;
  thread_summary?: string | null;
  focus?: StrategyRdCoachFocus | null;
  /** The open hypothesis (saved backtest) — the BE adds its results for TES. */
  hypothesis_id?: string | null;
  /** The page's template / account settings, including unsaved edits. */
  template?: Record<string, unknown> | null;
  sizing?: Record<string, unknown> | null;
  /** Up to 2 PNG / JPEG / WebP, 4 MB in total. */
  images?: StrategyRdCoachImage[] | null;
  /** "Ask TES about this": the candle's open time (ISO) and timeframe. */
  chart_focus?: { time: string; candle_type?: string | null } | null;
  /** The Lab's current market — lets TES backtest a recipe before it's saved. */
  market?: StrategyRdCoachMarket | null;
}

export interface StrategyRdCoachMarket {
  symbol: string;
  candle_type: string;
  lookback_days: number;
  asset_class?: string | null;
}

export interface StrategyRdCoachImage {
  mime_type: "image/png" | "image/jpeg" | "image/webp" | string;
  data_base64: string;
}

/** A finished TES backtest, as the chat shows it (`quant_eval.card`). */
export interface StrategyRdCoachResultCard {
  hypothesis_id: string;
  name?: string | null;
  version?: number | null;
  stats: StrategyRdHeadlineStats;
  by_side?: Record<string, StrategyRdHeadlineStats> | null;
  split?: StrategyRdHoldoutSplit | null;
  /** Card copy of `overview.stability` — halves + concentration (no monthly path). */
  stability?: Pick<StrategyRdStability, "halves" | "concentration"> | null;
  equity_curve: Array<{ t: string; equity: number }>;
  warnings?: string[] | null;
  robustness?: {
    verdict?: string | null;
    stale?: boolean;
    /** Verdict only (`ok` / `fragile_to_costs`). */
    cost_stress?: string | null;
  } | null;
  sweep?: StrategyRdSweepTable | null;
  market?: { symbol?: string | null; candle_type?: string | null } | null;
  /** Dates the backtest covers (YYYY-MM-DD). */
  period?: { start?: string | null; end?: string | null } | null;
  /** Net P&L in account currency. */
  net_pnl?: number | null;
}

/** A backtest / robustness / parameter search TES started this turn. */
export interface StrategyRdCoachLabAction {
  kind: "backtest" | "robustness" | "sweep" | "search" | "account" | string;
  job_id: string;
  status: "queued" | "running" | "ready" | "failed" | string;
  poll_url?: string | null;
  hypothesis_id?: string | null;
  /** `{stage, done?, total?}` while running. */
  progress?: { stage: string; done?: number | null; total?: number | null } | null;
  /** e.g. `backtested`, `searched`, `no_setups`. */
  outcome?: string | null;
  card?: StrategyRdCoachResultCard | null;
  /** FE: the status text while polling, or the failure / no-setups message. */
  message?: string | null;
}

export interface StrategyRdCoachPatchOp {
  op?: string;
  [key: string]: unknown;
}

export interface StrategyRdCoachComposerPatch {
  ops: StrategyRdCoachPatchOp[];
}

export interface StrategyRdCoachTurnResponse {
  reply_markdown: string;
  suggested_chips?: StrategyRdCoachChip[] | null;
  composer_patch?: StrategyRdCoachComposerPatch | null;
  /**
   * Preferred on Confirm: the Lab template after TES merged its rule changes.
   * Replace `page.template` with this — do not copy `composer.entry.mode` alone.
   */
  template?: Record<string, unknown> | null;
  /**
   * A custom rule TES compiled (`compile_rule` → `template_patch`, e.g. `{stop: {rule}}`).
   * Used when `template` is absent: deep-merge into the page template.
   */
  template_patch?: Record<string, unknown> | null;
  /** Preferred on Confirm: full merged sizing (replace). */
  sizing?: Record<string, unknown> | null;
  /** Sizing, leverage, costs, position mode, daily limits — deep-merged when `sizing` is absent. */
  sizing_patch?: Record<string, unknown> | null;
  /** Plain-English lines for the Confirm card ("Stop: 0.1 ATR beyond …"). */
  patch_readback?: string[] | null;
  lab_actions?: StrategyRdCoachLabAction[] | null;
  usage?: Partial<StrategyRdCoachUsage> | null;
  source?: string | null;
  catalog_only?: boolean;
  persisted?: boolean;
  thread_id?: string | null;
  disclaimer?: string | null;
  product_name?: string | null;
  product_expand?: string | null;
}

export interface StrategyRdCoachValidatePatchRequest {
  composer?: StrategyRdCoachComposerBody | Record<string, unknown> | null;
  base?: Record<string, unknown> | null;
  composer_patch: StrategyRdCoachComposerPatch;
}

export interface StrategyRdCoachValidatePatchResponse {
  ok?: boolean;
  composer?: StrategyRdCoachComposerBody | Record<string, unknown> | null;
  error?: string | null;
  [key: string]: unknown;
}

export interface StrategyRdCoachWowSeedResponse {
  wow?: boolean;
  symbol?: string | null;
  window?: {
    start?: string;
    end?: string;
    lookback_days?: number;
  } | null;
  style_id?: string | null;
  intake?: StrategyRdCoachIntakeResponse | null;
  next_steps?: string[] | null;
  opening_chips?: StrategyRdCoachChip[] | null;
  catalog_only?: boolean;
}

export interface StrategyRdCoachJournalHintsRequest {
  dominant_session?: string | null;
  symbol_class?: string | null;
  loss_hour_utc?: number | null;
  note?: string | null;
}

export interface StrategyRdCoachJournalHintsResponse {
  chips?: StrategyRdCoachChip[] | null;
  suggested_chips?: StrategyRdCoachChip[] | null;
  [key: string]: unknown;
}

export interface StrategyRdCoachThreadSummary {
  id: string;
  title: string;
  thread_summary?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  message_count?: number | null;
  /** Backtests this chat ran or discussed, oldest first (the last is the latest). */
  hypothesis_ids?: string[];
}

export interface StrategyRdCoachThreadMessage {
  id?: string;
  role: "user" | "assistant" | string;
  content: string;
  meta?: Record<string, unknown> | null;
  created_at?: string | null;
}

export interface StrategyRdCoachLabSnapshot {
  composer?: StrategyRdCoachComposerBody | Record<string, unknown> | null;
  goal?: string | null;
  style_id?: string | null;
  pack_id?: string | null;
  last_job_id?: string | null;
  scan_summary?: StrategyRdCoachScanSummary | null;
  outcome_summary?: StrategyRdOutcomeSummary | Record<string, unknown> | null;
  focus?: StrategyRdCoachFocus | null;
  [key: string]: unknown;
}

export interface StrategyRdCoachThreadDetail extends StrategyRdCoachThreadSummary {
  messages: StrategyRdCoachThreadMessage[];
  lab_snapshot?: StrategyRdCoachLabSnapshot | null;
}

export interface StrategyRdCoachThreadsListResponse {
  threads: StrategyRdCoachThreadSummary[];
}

/** One thing TES did during a turn ("Checking the recipe", "Running the backtest on BTCUSDT 5m"). */
export interface StrategyRdCoachActivityStep {
  label: string;
  /** Server time (s) the step started. */
  at: number;
}

export interface StrategyRdCoachActivity {
  steps: StrategyRdCoachActivityStep[];
  started?: number | null;
  done?: boolean;
  finished?: number | null;
  /** Server clock when read (s). */
  now?: number | null;
}

/** A finished turn's steps, kept on its reply ("Worked for 24s · 5 steps"). */
export interface StrategyRdCoachTurnTrace {
  steps: { label: string; seconds: number }[];
  seconds: number;
}

export interface StrategyRdCoachUiMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Assistant: what TES did to produce this reply. */
  activity?: StrategyRdCoachTurnTrace | null;
  suggested_chips?: StrategyRdCoachChip[] | null;
  composer_patch?: StrategyRdCoachComposerPatch | null;
  source?: string | null;
  /** One Confirm card per reply: all three proposals and their readback. */
  proposal?: StrategyRdCoachProposal | null;
  lab_actions?: StrategyRdCoachLabAction[] | null;
  /** User message: attached screenshots (data URLs, for the bubble only). */
  image_previews?: string[] | null;
}

export interface StrategyRdCoachProposal {
  composer_patch?: StrategyRdCoachComposerPatch | null;
  /** Full merged template — Confirm replaces the page template. */
  template?: Record<string, unknown> | null;
  template_patch?: Record<string, unknown> | null;
  /** Full merged sizing — Confirm replaces page sizing. */
  sizing?: Record<string, unknown> | null;
  sizing_patch?: Record<string, unknown> | null;
  patch_readback?: string[] | null;
  /** Confirmed / dismissed — the card stays as a record, without buttons. */
  resolved?: "confirmed" | "dismissed" | null;
}

export interface StrategyRdCoachProfileOption {
  id: string;
  label: string;
  hint?: string | null;
  style_id?: string | null;
}

export interface StrategyRdCoachProfileQuestion {
  id: string;
  prompt: string;
  multi: boolean;
  required: boolean;
  options: StrategyRdCoachProfileOption[];
}

export interface StrategyRdCoachProfileAnswers {
  experience?: string | null;
  trading_horizon?: string | null;
  instrument_classes?: string[] | null;
  strategy_kinds?: string[] | null;
  preferred_sessions?: string[] | null;
  component_families?: string[] | null;
  preferred_style_id?: string | null;
  coaching_verbosity?: string | null;
  suggested_candle_type?: string | null;
  alt_candle_types?: string[] | null;
  suggested_lookback_days?: number | null;
  example_symbols?: string[] | null;
  [key: string]: unknown;
}

export interface StrategyRdCoachProfileResponse {
  user_id?: string | null;
  answers: StrategyRdCoachProfileAnswers;
  complete: boolean;
  completed_at?: string | null;
  updated_at?: string | null;
  focus?: StrategyRdCoachFocus | null;
  lab_seed?: StrategyRdCoachLabSeed | null;
  orientation_starter_chips?: StrategyRdCoachChip[] | null;
  product_name?: string | null;
  product_expand?: string | null;
  disclaimer?: string | null;
  catalog_only?: boolean;
  questionnaire: {
    questions: StrategyRdCoachProfileQuestion[];
    catalog_only?: boolean;
    note?: string | null;
  };
}

